/**
 * Compact horizontal bar chart for category spend in Mojo Agent chat.
 */
import React, { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { ChatCategorySpendChartMetadata } from '../../types/ai-chat.types';

const BAR_COLORS = [
  '#8B5CF6',
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EC4899',
  '#0EA5E9',
  '#F97316',
  '#6366F1',
  '#14B8A6',
  '#EF4444',
  '#84CC16',
  '#A855F7',
];

const MAX_DISPLAY_BARS = 12;

function formatAud(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(amount);
}

interface ChatCategorySpendChartProps {
  chart: ChatCategorySpendChartMetadata;
}

export function ChatCategorySpendChart({ chart }: ChatCategorySpendChartProps): React.ReactElement {
  const { displaySeries, truncatedCount } = useMemo(() => {
    const series = chart.series ?? [];
    if (series.length <= MAX_DISPLAY_BARS) {
      return { displaySeries: series, truncatedCount: 0 };
    }
    return {
      displaySeries: series.slice(0, MAX_DISPLAY_BARS),
      truncatedCount: series.length - MAX_DISPLAY_BARS,
    };
  }, [chart.series]);

  const chartHeight = Math.max(160, displaySeries.length * 28 + 40);
  const ariaSummary = `${chart.title}. Total ${formatAud(chart.total)} across ${chart.transactionCount} transactions. Top categories: ${displaySeries
    .map((s) => `${s.name} ${formatAud(s.amount)} (${s.count})`)
    .join('; ')}`;

  return (
    <div
      className="mt-3 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/80 p-2"
      data-testid="mojo-category-spend-chart"
      role="img"
      aria-label={ariaSummary}
    >
      <div className="mb-2 px-1">
        <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 leading-snug">{chart.title}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {formatAud(chart.total)} · {chart.transactionCount} transaction
          {chart.transactionCount !== 1 ? 's' : ''}
        </p>
      </div>

      <div style={{ width: '100%', height: chartHeight }} className="min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={displaySeries}
            layout="vertical"
            margin={{ top: 4, right: 12, left: 4, bottom: 4 }}
          >
            <XAxis
              type="number"
              tick={{ fontSize: 10, fill: 'currentColor' }}
              className="text-gray-500"
              tickFormatter={(v) => `$${Number(v).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={88}
              tick={{ fontSize: 10, fill: 'currentColor' }}
              className="text-gray-600 dark:text-gray-300"
              interval={0}
            />
            <Tooltip
              formatter={(value: number, _name, props) => {
                const count = (props?.payload as { count?: number } | undefined)?.count ?? 0;
                return [`${formatAud(Number(value) || 0)} (${count} tx)`, 'Spend'];
              }}
              labelStyle={{ fontWeight: 600 }}
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: '1px solid #e5e7eb',
              }}
            />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]} maxBarSize={18}>
              {displaySeries.map((_, index) => (
                <Cell key={`cell-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {truncatedCount > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 px-1 mt-1">
          and {truncatedCount} more categor{truncatedCount === 1 ? 'y' : 'ies'}
        </p>
      )}

      {/* Screen-reader / text fallback */}
      <ul className="sr-only">
        {displaySeries.map((s) => (
          <li key={s.name}>
            {s.name}: {formatAud(s.amount)}, {s.count} transactions
          </li>
        ))}
      </ul>
    </div>
  );
}
